/**
 * The chips every Changes surface shares: a state (the five words of the
 * process), a size (the three words), a pull request, a scenario tag. One set,
 * so the list, the change page and the Hub card print the same word in the
 * same colour for the same fact.
 */
import type { ComponentType } from 'react';
import { CheckCircle2, CircleDashed, Clock, GitMerge, GitPullRequest, Hammer } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  SIZE_RULE,
  formatDay,
  scenarioTag,
  type ChangeSize,
  type ChangeState,
  type PullRequest,
  type ScenarioState,
} from '@/lib/changes/model';

const STATE_STYLE: Record<ChangeState, { cls: string; Icon: ComponentType<{ className?: string }> }> = {
  'not yet': {
    cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    Icon: CircleDashed,
  },
  'waiting for your review': {
    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    Icon: Clock,
  },
  passed: {
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    Icon: CheckCircle2,
  },
  'being built': {
    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    Icon: Hammer,
  },
  done: {
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    Icon: CheckCircle2,
  },
};

export function StateBadge({ state, className }: { state: ChangeState; className?: string }) {
  const { cls, Icon } = STATE_STYLE[state];
  return (
    <span
      data-state={state}
      className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', cls, className)}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {state}
    </span>
  );
}

const SIZE_STYLE: Record<ChangeSize, string> = {
  small: 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300',
  ordinary: 'border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-300',
  foundational: 'border-fuchsia-300 text-fuchsia-700 dark:border-fuchsia-700 dark:text-fuchsia-300',
};

export function SizeBadge({ size, className }: { size: ChangeSize; className?: string }) {
  return (
    <span
      data-size={size}
      title={SIZE_RULE[size]}
      className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize', SIZE_STYLE[size], className)}
    >
      {size}
    </span>
  );
}

/** owner/repo#n, linked, with what happened to it and when. The link is the action. */
export function PrChip({ pr, className }: { pr: PullRequest; className?: string }) {
  const Icon = pr.state === 'merged' ? GitMerge : GitPullRequest;
  const when = pr.state === 'merged' && pr.mergedAt ? `merged ${formatDay(pr.mergedAt)}` : `open since ${formatDay(pr.openedAt)}`;
  return (
    <a
      href={pr.url}
      target="_blank"
      rel="noreferrer"
      data-pr={`${pr.repo}#${pr.number}`}
      title={`${pr.title} (into ${pr.base})`}
      className={cn('inline-flex flex-wrap items-center gap-1.5 text-xs text-slate-600 hover:underline dark:text-slate-400', className)}
    >
      <Icon className={cn('h-3.5 w-3.5 shrink-0', pr.state === 'merged' ? 'text-violet-600 dark:text-violet-400' : 'text-emerald-600 dark:text-emerald-400')} />
      <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] text-slate-700 dark:bg-slate-800 dark:text-slate-300">
        {pr.repo}#{pr.number}
      </code>
      <span>{when}</span>
    </a>
  );
}

export function TagChip({ state }: { state: ScenarioState }) {
  return (
    <code
      data-scenario-state
      data-tag={state}
      className={cn(
        'rounded px-1.5 py-0.5 font-mono text-[11px]',
        state === 'built'
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
          : 'border border-dashed border-slate-300 text-slate-500 dark:border-slate-600 dark:text-slate-400',
      )}
    >
      {scenarioTag(state)}
    </code>
  );
}
